import { isGroup, isRow } from "../sections";
import type { EmailDocument, Row, Selection } from "../types";
import { selectionEquals } from "./equals";

function appendRowTabStops(stops: Selection[], row: Row): void {
  stops.push({ kind: 'row', rowId: row.id });
  for (const column of row.columns) {
    stops.push({ kind: 'column', rowId: row.id, columnId: column.id });
    for (const block of column.blocks) {
      stops.push({
        kind: 'block',
        rowId: row.id,
        columnId: column.id,
        blockId: block.id,
      });
      if (block.type === 'buttonRow') {
        for (let slotIndex = 0; slotIndex < block.buttons.length; slotIndex++) {
          stops.push({
            kind: 'buttonRowSlot',
            rowId: row.id,
            columnId: column.id,
            blockId: block.id,
            slotIndex,
          });
        }
      }
    }
  }
}

export function flattenCanvasTabStops(document: EmailDocument): Selection[] {
  const stops: Selection[] = [];
  for (const section of document.sections) {
    if (isGroup(section)) {
      stops.push({ kind: 'group', groupId: section.id });
      for (const row of section.rows) {
        appendRowTabStops(stops, row);
      }
    } else if (isRow(section)) {
      appendRowTabStops(stops, section);
    }
  }
  return stops;
}

function tabStopIndex(stops: Selection[], selection: Selection): number {
  return stops.findIndex(stop => selectionEquals(stop, selection));
}

export function adjacentCanvasTabSelection(
  document: EmailDocument,
  selection: Selection,
  direction: -1 | 1,
): Selection {
  const stops = flattenCanvasTabStops(document);
  if (stops.length === 0) {
    return { kind: 'email' };
  }

  if (selection.kind === 'email') {
    return direction > 0 ? stops[0] : stops[stops.length - 1];
  }

  const index = tabStopIndex(stops, selection);
  if (index < 0) {
    return direction > 0 ? stops[0] : stops[stops.length - 1];
  }

  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= stops.length) {
    return { kind: 'email' };
  }
  return stops[nextIndex];
}

export function escapeCanvasSelection(selection: Selection): Selection {
  if (selection.kind === 'buttonRowSlot') {
    return {
      kind: 'block',
      rowId: selection.rowId,
      columnId: selection.columnId,
      blockId: selection.blockId,
    };
  }
  if (selection.kind === 'email') {
    return selection;
  }
  return { kind: 'email' };
}
